import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import { useMyMonitoringDeleteSiteZone } from '@apps-common/apis/my-monitoring/zones/delete-site-zone'
import { useMyMonitoringGetSiteZone } from '@apps-common/apis/my-monitoring/zones/get-site-zone'
import { useMyMonitoringUpdateSiteZone } from '@apps-common/apis/my-monitoring/zones/update-site-zone'
import { useDeleteUnitCustomZone, useGetUnitCustomZone, useUpdateUnitCustomZone } from '@apps-common/apis/portal/zones'
import { ButtonRoundedSm } from '@apps-common/components/elements/button'
import { InputSM } from '@apps-common/components/elements/form'
import { ConfirmModalAction } from '@apps-common/components/modals'
import { ModalBody, ModalFooter, ModalTopConfirmActionButton } from '@apps-common/components/modals/modal'
import { ModalDismiss } from '@apps-common/config/modal'
import { FORM_DEFAULT_REQUIRED } from '@apps-common/config/react-hook-form'
import { useProjectHook } from '@apps-common/hooks/useProjectHook'
import { IUnitCustomZoneModel } from '@apps-common/types/portal/unit'

interface IProps {
  zoneId: number
  unitId: string
  closeCb: (role: string) => void
}

export const ZoneUpdateForm = ({ zoneId, unitId, closeCb }: IProps) => {
  const useGetZoneHook = useProjectHook(useGetUnitCustomZone, useMyMonitoringGetSiteZone)
  const useUpdateZoneHook = useProjectHook(useUpdateUnitCustomZone, useMyMonitoringUpdateSiteZone)
  const useDeleteZoneHook = useProjectHook(useDeleteUnitCustomZone, useMyMonitoringDeleteSiteZone)

  const { data: zone, isLoading: zoneIsLoading, isError: zoneIsError } = useGetZoneHook({ zoneId })
  const { mutate: updateZone, isPending: updateIsPending } = useUpdateZoneHook(true, () => closeCb(ModalDismiss.Confirm))
  const { mutate: deleteZone, isPending: deleteIsPending } = useDeleteZoneHook(true, () => closeCb(ModalDismiss.Confirm))

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
    reset
  } = useForm<IUnitCustomZoneModel>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      unitCustomZoneDescriptionId: zoneId,
      unitId,
      zone: '',
      description: '',
      mapPartition: false,
      partition: '',
      editedBy: '',
      editDate: new Date().toISOString()
    }
  })

  useEffect(() => {
    if (zone) {
      reset(zone)
    }
  }, [zone, reset])

  const submit = (values: IUnitCustomZoneModel) => {
    if (values.partition && values.partition.length > 0) {
      values.mapPartition = true
    }

    updateZone({ model: values })
  }

  const isLoading = zoneIsLoading || zoneIsError
  const isUpdating = isSubmitting || updateIsPending || deleteIsPending

  return (
    <>
      <ModalBody classes='!pt-[90px]'>
        <div className='flex items-center justify-end'>
          <ModalTopConfirmActionButton
            loading={isLoading || isUpdating}
            confirmProps={{
              text: 'Remove zone?',
              description: 'Are you sure you would like to remove zone description from this site?',
              action: ConfirmModalAction.Error
            }}
            confirmCallback={() => deleteZone({ zoneId })}
            text='Delete'
            icon='delete'
            classes='action-error'
          />
        </div>
        <form noValidate id='unit-zone-update-form' onSubmit={handleSubmit(submit)}>
          <InputSM
            key='description'
            type='text'
            placeholder='Enter description'
            label='Zone description'
            id='description'
            showLabel
            loading={isLoading}
            disabled={isUpdating}
            registration={register('description', {
              required: FORM_DEFAULT_REQUIRED,
              maxLength: {
                value: 30,
                message: 'No longer than 30 characters'
              }
            })}
            errors={errors.description}
          />
          <InputSM
            key='zone'
            type='text'
            placeholder='1 to 999'
            label='User number (Zone)'
            id='zone'
            showLabel
            loading={isLoading}
            disabled={isUpdating}
            registration={register('zone', {
              required: FORM_DEFAULT_REQUIRED,
              pattern: {
                value: /^[0-9]{3}$/,
                message: '3 numerical characters only e.g. 001'
              }
            })}
            errors={errors.zone}
          />
          <InputSM
            key='partition'
            type='text'
            placeholder='0 to 99'
            label='Partition (If required)'
            id='userPartition'
            showLabel
            loading={isLoading}
            disabled={isUpdating}
            registration={register('partition', {
              required: false,
              pattern: {
                value: /^[0-9]{2}$/,
                message: '2 numerical characters only e.g. 01'
              }
            })}
            errors={errors.partition}
          />
        </form>
      </ModalBody>
      <ModalFooter>
        <div className='flex items-center justify-between'>
          <ButtonRoundedSm
            type='submit'
            form='unit-zone-update-form'
            loading={isLoading || isUpdating}
            text='Update'
            className='action-success'
          />
          <ButtonRoundedSm
            type='button'
            loading={isLoading || isUpdating}
            text='Cancel'
            className='action-neutral'
            callback={() => closeCb(ModalDismiss.Cancel)}
          />
        </div>
      </ModalFooter>
    </>
  )
}
